import { useEffect, useState, useCallback, useRef } from 'react';
/**
 * An async-utility hook that accepts a callback function and a delay time (in milliseconds), then repeats the
 * execution of the given function by the defined milliseconds.
 */
const useInterval = (
  fn: () => void | Promise<void>,
  milliseconds: number
): { isCleared: boolean; clear: () => void } => {
  const timeout = useRef<number>();
  const callback = useRef(fn);
  const [isCleared, setIsCleared] = useState(false);

  // the clear method
  const clear = useCallback(() => {
    if (timeout.current) {
      clearInterval(timeout.current);
      setIsCleared(true);
    }
  }, []);

  // if the provided function changes, change its reference
  useEffect(() => {
    if (typeof fn === 'function') {
      callback.current = fn;
    }
  }, [fn]);

  // when the milliseconds change, reset the timeout
  useEffect(() => {
    if (typeof milliseconds === 'number') {
      timeout.current = window.setInterval(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        callback.current();
      }, milliseconds);
    }
  }, [milliseconds]);

  // when component unmount clear the timeout
  useEffect(
    () => () => {
      clear();
    },
    [clear]
  );

  return { isCleared, clear };
};

export default useInterval;
